import React, { useEffect, useState } from 'react';
import { AlertCircle, ArrowUpRight, Plus, Trash2 } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentMethods } from '../../../../../hooks/usePaymentMethods';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import { JournalLine, postJournalEntry } from '../../../../../services/JournalEntriesService';
import { formatEuro } from '../masters/financeMasters';
import { AttachmentsField, FormSkeleton, JournalPreview, SearchSelect, today } from './FormParts';
import '../../shared-modules.css';
import './FinanceForms.css';

// La tarjeta es la de débito de la empresa: sale del banco
const PAY_ACCOUNTS = [
  { id: 'caja', code: '570000', label: '570000 · Caja, euros', name: 'Caja, euros' },
  { id: 'banco', code: '572000', label: '572000 · Bancos c/c', name: 'Bancos c/c' },
  { id: 'tarjeta', code: '572000', label: 'Tarjeta de débito (572000)', name: 'Bancos c/c (tarjeta de débito)' }
];

const VAT_OPTIONS = [0, 4, 10, 21];

const round2 = (n: number) => Math.round(n * 100) / 100;

interface ExpenseLine {
  id: string;
  account: string;
  description: string;
  amount: number;
}

const newExpenseLine = (account = ''): ExpenseLine => ({
  id: String(Date.now() + Math.random()),
  account,
  description: '',
  amount: 0
});

interface DirectExpenseFormProps {
  privacyMode: boolean;
  onDone?: () => void;
}

export const DirectExpenseForm = ({ privacyMode, onDone }: DirectExpenseFormProps) => {
  const { notify } = useToast();

  const { methods } = usePaymentMethods();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);

  const [beneficiary, setBeneficiary] = useState('');
  const [date, setDate] = useState(today());
  const [payAccount, setPayAccount] = useState('caja');
  const [paymentMethod, setPaymentMethod] = useState('Efectivo (Caja)');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<ExpenseLine[]>([newExpenseLine()]);
  const [vatRate, setVatRate] = useState(21);
  const [attachments, setAttachments] = useState<File[]>([]);

  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const chart = await listChartOfAccounts();
        if (cancelled) return;
        setAccounts(chart);
        // Otros servicios: el gasto menor más habitual
        const other = chart.find(a => a.code.startsWith('629'));
        if (other) setLines([newExpenseLine(other.code)]);
        setLoadError('');
      } catch (err) {
        console.error('Error loading direct expense form data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudo cargar el plan de cuentas.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // El método propone la cuenta de pago: efectivo a caja, tarjeta a tarjeta, el resto a banco
  const handleMethodChange = (name: string) => {
    setPaymentMethod(name);
    const method = methods.find(m => m.name === name);
    if (!method) return;
    if (method.account === '570000') setPayAccount('caja');
    else if (/tarjeta/i.test(method.name)) setPayAccount('tarjeta');
    else setPayAccount('banco');
  };

  const updateLine = (id: string, patch: Partial<ExpenseLine>) =>
    setLines(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l)));

  const pay = PAY_ACCOUNTS.find(p => p.id === payAccount) || PAY_ACCOUNTS[0];
  const base = round2(lines.reduce((s, l) => s + l.amount, 0));
  const vatAmount = round2(base * vatRate / 100);
  const total = round2(base + vatAmount);

  const accountName = (code: string) => {
    const account = accounts.find(a => a.code === code);
    return account ? account.name : 'Cuenta de gasto';
  };

  const journalLines: JournalLine[] = [
    ...lines.map(l => ({ account: l.account, accountName: accountName(l.account), debit: l.amount, credit: 0 })),
    { account: '472000', accountName: 'H.P. IVA Soportado', debit: vatAmount, credit: 0 },
    { account: pay.code, accountName: pay.name, debit: 0, credit: total }
  ];

  const validate = () => {
    if (!beneficiary.trim()) return 'Indique el beneficiario o establecimiento.';
    if (!date) return 'La fecha es obligatoria.';
    if (lines.some(l => !l.account)) return 'Todas las líneas necesitan una cuenta de gasto.';
    if (lines.some(l => !l.description.trim())) return 'Todas las líneas necesitan una descripción.';
    if (lines.some(l => l.amount <= 0)) return 'Todas las líneas necesitan un importe mayor que cero.';
    return '';
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const ref = reference.trim() ? ` (${reference.trim()})` : '';
      await postJournalEntry(`Gasto directo: ${beneficiary.trim()}${ref}`, journalLines, 'Gastos Directos', accounts, date);
    } catch (err) {
      console.error('Error posting direct expense journal entry:', err);
      setFormError(`No se pudo contabilizar el gasto: ${err instanceof Error ? err.message : 'error del servidor.'}`);
      setSaving(false);
      return;
    }
    notify('Gasto contabilizado', `Gasto de ${formatEuro(total, privacyMode)} en ${beneficiary.trim()} contabilizado en el Libro Diario [6xx + 472 a ${pay.code.slice(0, 3)}].`, 'info');
    setSaving(false);
    if (onDone) onDone();
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const expenseOptions = accounts
    .filter(a => a.code.startsWith('6') && a.code.length >= 3)
    .map(a => ({ value: a.code, label: `${a.code} · ${a.name}` }));

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">COMPRAS Y PAGOS • GASTOS DIRECTOS</span>
          <h3 className="fin-doc__title"><ArrowUpRight size={14} /> Gasto directo</h3>
        </div>
        <span className="fin-doc__badge">PAGADO CON {pay.code}</span>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Beneficiario *</label>
          <input
            type="text"
            className="sidebar-form-input"
            value={beneficiary}
            onChange={(e) => setBeneficiary(e.target.value)}
            placeholder="Establecimiento o persona que cobra"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Referencia</label>
          <input
            type="text"
            className="sidebar-form-input"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Nº de ticket o justificante"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Método *</label>
          <select className="sidebar-form-input" value={paymentMethod} onChange={(e) => handleMethodChange(e.target.value)}>
            {methods.filter(m => m.isActive || m.name === paymentMethod).map(m => (
              <option key={m.id} value={m.name}>{m.name}</option>
            ))}
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cuenta de pago *</label>
          <select className="sidebar-form-input" value={payAccount} onChange={(e) => setPayAccount(e.target.value)}>
            {PAY_ACCOUNTS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">IVA *</label>
          <select className="sidebar-form-input" value={vatRate} onChange={(e) => setVatRate(parseInt(e.target.value, 10) || 0)}>
            {VAT_OPTIONS.map(r => <option key={r} value={r}>{r}%</option>)}
          </select>
        </div>
      </div>

      <div className="fin-lines">
        <div className="fin-lines__header">
          <span>Líneas del gasto</span>
          <button type="button" className="fin-lines__add" onClick={() => setLines([...lines, newExpenseLine(lines[lines.length - 1].account)])}>
            <Plus size={12} /> AÑADIR LÍNEA
          </button>
        </div>

        <div className="fin-lines__row fin-lines__row--head fin-lines__row--expense">
          <span>Cuenta / categoría PGC</span>
          <span>Descripción</span>
          <span className="right">Importe (€)</span>
          <span />
        </div>

        {lines.map(line => (
          <div key={line.id} className="fin-lines__row fin-lines__row--expense">
            <SearchSelect
              options={expenseOptions}
              value={line.account}
              onChange={(account) => updateLine(line.id, { account })}
              placeholder="Buscar cuenta 6xx..."
              emptyText="No hay cuentas de gasto"
            />
            <input
              type="text"
              className="sidebar-form-input"
              value={line.description}
              onChange={(e) => updateLine(line.id, { description: e.target.value })}
              placeholder="Concepto del gasto"
            />
            <input
              type="number"
              min={0}
              step="0.01"
              className="sidebar-form-input right"
              value={line.amount}
              onChange={(e) => updateLine(line.id, { amount: parseFloat(e.target.value) || 0 })}
            />
            <button
              type="button"
              className="fin-lines__remove"
              title="Eliminar línea"
              disabled={lines.length === 1}
              onClick={() => setLines(lines.filter(l => l.id !== line.id))}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      <div className="fin-doc__totals">
        <span>Base imponible: <strong>{formatEuro(base, privacyMode)}</strong></span>
        <span>IVA: <strong>{vatRate}%</strong></span>
        <span>Cuota: <strong>{formatEuro(vatAmount, privacyMode)}</strong></span>
        <span className="fin-doc__total">Total: <strong>{formatEuro(total, privacyMode)}</strong></span>
      </div>

      <AttachmentsField files={attachments} onChange={setAttachments} />

      <JournalPreview lines={journalLines} privacyMode={privacyMode} />

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn" disabled title="Pendiente de endpoint">
          Guardar
        </button>
        <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled={saving} onClick={handleSubmit}>
          {saving ? 'Contabilizando...' : 'Contabilizar'}
        </button>
      </div>
    </div>
  );
};

export default DirectExpenseForm;
